/**
 * Import History Utility
 * 
 * Tracks import activities, provides rollback functionality,
 * and maintains audit trails for Crown Jewels imports
 */

const IMPORT_HISTORY_KEY = 'crownJewelImportHistory';
const ROLLBACK_WINDOW_MINUTES = 5; // Time window for rollback operations

/**
 * Record an import operation
 * @param {Object} importData - Import details
 * @returns {string} Import ID for reference
 */
export const recordImport = (importData) => {
  const importId = generateImportId();
  const timestamp = new Date().toISOString();
  
  const importRecord = {
    id: importId,
    timestamp,
    clientId: importData.clientId,
    clientName: importData.clientName,
    fileName: importData.fileName,
    fileType: importData.fileType,
    totalRows: importData.totalRows,
    successfulRows: importData.successfulRows,
    failedRows: importData.failedRows,
    importedAssetIds: importData.importedAssetIds || [],
    errors: importData.errors || [],
    warnings: importData.warnings || [],
    user: getCurrentUser(),
    canRollback: true,
    rollbackExpiresAt: new Date(Date.now() + ROLLBACK_WINDOW_MINUTES * 60 * 1000).toISOString(),
    status: 'completed'
  };
  
  // Get existing history
  const history = getImportHistory();
  
  // Add new record to the beginning
  history.unshift(importRecord);
  
  // Keep only last 50 imports
  const limitedHistory = history.slice(0, 50);
  
  // Save to localStorage
  localStorage.setItem(IMPORT_HISTORY_KEY, JSON.stringify(limitedHistory));
  
  return importId;
};

/**
 * Get import history for a specific client or all clients
 * @param {string} clientId - Optional client ID to filter by
 * @returns {Array} Array of import records
 */
export const getImportHistory = (clientId = null) => {
  try {
    const history = JSON.parse(localStorage.getItem(IMPORT_HISTORY_KEY) || '[]');
    
    if (clientId) {
      return history.filter(record => record.clientId === clientId);
    }
    
    return history;
  } catch (error) {
    console.error('Error loading import history:', error);
    return [];
  }
};

/**
 * Get a specific import record by ID
 * @param {string} importId - Import ID to find
 * @returns {Object|null} Import record or null if not found
 */
export const getImportRecord = (importId) => {
  const history = getImportHistory();
  return history.find(record => record.id === importId) || null;
};

/**
 * Check if an import can be rolled back
 * @param {string} importId - Import ID to check
 * @returns {boolean} Whether rollback is possible
 */
export const canRollback = (importId) => {
  const record = getImportRecord(importId);
  
  if (!record) return false;
  if (!record.canRollback) return false;
  if (record.status === 'rolled_back') return false;
  
  // Check if rollback window has expired
  const rollbackExpiry = new Date(record.rollbackExpiresAt);
  const now = new Date();
  
  return now < rollbackExpiry;
};

/**
 * Rollback an import operation
 * @param {string} importId - Import ID to rollback
 * @returns {Object} Rollback result
 */
export const rollbackImport = (importId) => {
  const record = getImportRecord(importId);
  
  if (!record) {
    return {
      success: false,
      error: 'Import record not found'
    };
  }
  
  if (!canRollback(importId)) {
    return {
      success: false,
      error: 'Import cannot be rolled back (expired or already rolled back)'
    };
  }
  
  try {
    // Get current assets
    const currentAssets = JSON.parse(localStorage.getItem('crownJewelAssets') || '[]');
    
    // Remove assets that were imported in this operation
    const rollbackAssets = currentAssets.filter(
      asset => !record.importedAssetIds.includes(asset.id)
    );
    
    // Save the updated assets
    localStorage.setItem('crownJewelAssets', JSON.stringify(rollbackAssets));
    
    // Update the import record to mark as rolled back
    updateImportRecord(importId, {
      status: 'rolled_back',
      rollbackTimestamp: new Date().toISOString(),
      canRollback: false
    });
    
    return {
      success: true,
      removedAssetsCount: record.importedAssetIds.length,
      message: `Successfully rolled back import. Removed ${record.importedAssetIds.length} assets.`
    };
  } catch (error) {
    console.error('Error during rollback:', error);
    return {
      success: false,
      error: `Rollback failed: ${error.message}`
    };
  }
};

/**
 * Update an existing import record
 * @param {string} importId - Import ID to update
 * @param {Object} updates - Updates to apply
 */
const updateImportRecord = (importId, updates) => {
  const history = getImportHistory();
  const recordIndex = history.findIndex(record => record.id === importId);
  
  if (recordIndex === -1) return;
  
  // Apply updates
  history[recordIndex] = { ...history[recordIndex], ...updates };
  
  // Save back to localStorage
  localStorage.setItem(IMPORT_HISTORY_KEY, JSON.stringify(history));
};

/**
 * Clean up expired rollback opportunities
 */
export const cleanupExpiredRollbacks = () => {
  const history = getImportHistory();
  const now = new Date();
  
  const updatedHistory = history.map(record => {
    if (record.canRollback && record.rollbackExpiresAt) {
      const expiry = new Date(record.rollbackExpiresAt);
      if (now > expiry) {
        return {
          ...record,
          canRollback: false
        };
      }
    }
    return record;
  });
  
  localStorage.setItem(IMPORT_HISTORY_KEY, JSON.stringify(updatedHistory));
};

/**
 * Get import statistics for a client
 * @param {string} clientId - Client ID
 * @returns {Object} Import statistics
 */
export const getImportStats = (clientId) => {
  const clientHistory = getImportHistory(clientId);
  
  const totalImports = clientHistory.length;
  const successfulImports = clientHistory.filter(record => 
    record.status === 'completed' && record.failedRows === 0
  ).length;
  const totalAssetsImported = clientHistory.reduce(
    (sum, record) => sum + (record.successfulRows || 0), 0
  );
  const lastImportDate = clientHistory.length > 0 ? clientHistory[0].timestamp : null;
  
  return {
    totalImports,
    successfulImports,
    totalAssetsImported,
    lastImportDate,
    successRate: totalImports > 0 ? (successfulImports / totalImports) * 100 : 0
  };
};

/**
 * Generate unique import ID
 * @returns {string} Unique import ID
 */
const generateImportId = () => {
  const timestamp = Date.now().toString(36);
  const randomStr = Math.random().toString(36).substring(2, 8);
  return `import_${timestamp}_${randomStr}`;
};

/**
 * Get current user identifier (placeholder for future user management)
 * @returns {string} User identifier
 */
const getCurrentUser = () => {
  // For now, return a generic identifier
  // In future, this could integrate with actual user management
  return localStorage.getItem('currentUser') || 'anonymous';
};

/**
 * Export import history as CSV
 * @param {string} clientId - Optional client ID to filter
 * @returns {string} CSV formatted history
 */
export const exportImportHistory = (clientId = null) => {
  const history = getImportHistory(clientId);
  
  let csv = 'Date,Client,File Name,File Type,Total Rows,Successful,Failed,Status,User\n';
  
  history.forEach(record => {
    const date = new Date(record.timestamp).toLocaleString();
    const clientName = record.clientName || 'Unknown';
    const fileName = record.fileName || 'Unknown';
    const fileType = record.fileType || 'Unknown';
    const totalRows = record.totalRows || 0;
    const successful = record.successfulRows || 0;
    const failed = record.failedRows || 0;
    const status = record.status || 'Unknown';
    const user = record.user || 'Unknown';
    
    csv += `"${date}","${clientName}","${fileName}","${fileType}",${totalRows},${successful},${failed},"${status}","${user}"\n`;
  });
  
  return csv;
};

/**
 * Get rollback status for recent imports
 * @param {string} clientId - Client ID
 * @returns {Array} Array of imports with rollback status
 */
export const getRollbackableImports = (clientId) => {
  const clientHistory = getImportHistory(clientId);
  
  return clientHistory
    .filter(record => record.status === 'completed')
    .map(record => ({
      id: record.id,
      timestamp: record.timestamp,
      fileName: record.fileName,
      successfulRows: record.successfulRows,
      canRollback: canRollback(record.id),
      rollbackExpiresAt: record.rollbackExpiresAt
    }))
    .slice(0, 5); // Show last 5 imports only
};

/**
 * Get detailed import summary
 * @param {string} importId - Import ID
 * @returns {Object} Detailed import information
 */
export const getImportSummary = (importId) => {
  const record = getImportRecord(importId);
  
  if (!record) return null;
  
  return {
    id: record.id,
    timestamp: record.timestamp,
    clientName: record.clientName,
    fileName: record.fileName,
    fileType: record.fileType,
    totalRows: record.totalRows,
    successfulRows: record.successfulRows,
    failedRows: record.failedRows,
    errorCount: record.errors?.length || 0,
    warningCount: record.warnings?.length || 0,
    status: record.status,
    canRollback: canRollback(record.id),
    rollbackExpiresAt: record.rollbackExpiresAt,
    user: record.user
  };
}; 